export default class Renderer {
  constructor(engine) {
    this._engine = engine;
    this._dirty = false;
    this._rafId = null;
    this._ctx = null;

  }

  markDirty() {
    if (this._dirty) return;
    this._dirty = true;
    if (this._rafId === null) {
      this._rafId = requestAnimationFrame(() => this._draw());
    }
  }

  _draw() {
    this._rafId = null;
    this._dirty = false;
    const engine = this._engine;
    const canvas = engine._canvas;
    if (!canvas) return;
    if (!this._ctx) this._ctx = canvas.getContext('2d');
    const ctx = this._ctx;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.width;
    const h = canvas.height;
    const camera = engine._camera;

    // beforeDrawing hook — raw context (identity transform)
    engine.emit('beforeDrawing', ctx);

    ctx.save();
    // Set camera transform: translate to center, then apply pan/zoom
    ctx.setTransform(
      camera.scale * dpr, 0,
      0, camera.scale * dpr,
      w / 2 - camera.x * camera.scale * dpr,
      h / 2 - camera.y * camera.scale * dpr
    );

    this._drawEdges(ctx, camera.scale);
    this._drawNodes(ctx, camera.scale);

    ctx.restore();

    // afterDrawing hook — camera transform applied
    ctx.save();
    ctx.setTransform(
      camera.scale * dpr, 0,
      0, camera.scale * dpr,
      w / 2 - camera.x * camera.scale * dpr,
      h / 2 - camera.y * camera.scale * dpr
    );
    engine.emit('afterDrawing', ctx);
    ctx.restore();
  }

  _drawEdges(ctx, scale) {
    const engine = this._engine;
    const physics = engine._physics;
    const edges = engine._edges.get();
    const opts = engine._options;
    const smooth = opts.edges?.smooth;
    const arrowEnabled = opts.edges?.arrows?.to?.enabled;
    const arrowScale = opts.edges?.arrows?.to?.scaleFactor || 1;
    const defaultEdgeColor = opts.edges?.color?.color || '#363636';
    const edgeWidth = opts.edges?.width || 1;
    const selectedEdges = engine._interaction?._selectedEdges || new Set();
    const hoveredEdge = engine._interaction?._hoveredEdge;

    for (let i = 0; i < edges.length; i++) {
      const edge = edges[i];
      const fromIdx = physics._idToIndex.get(edge.from);
      const toIdx = physics._idToIndex.get(edge.to);
      if (fromIdx === undefined || toIdx === undefined) continue;

      const x1 = physics._pos[fromIdx * 2];
      const y1 = physics._pos[fromIdx * 2 + 1];
      const x2 = physics._pos[toIdx * 2];
      const y2 = physics._pos[toIdx * 2 + 1];

      // Resolve edge color
      let color = edge.color?.color || edge.color || defaultEdgeColor;
      if (selectedEdges.has(edge.id)) {
        color = opts.edges?.color?.highlight || '#32CD32';
      } else if (hoveredEdge === edge.id) {
        color = opts.edges?.color?.hover || '#32CD32';
      }

      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = edgeWidth;

      let midX, midY;
      if (smooth && smooth !== false && typeof smooth === 'object') {
        // Quadratic bezier curve
        midX = (x1 + x2) / 2;
        midY = (y1 + y2) / 2;
        const dx = x2 - x1;
        const dy = y2 - y1;
        const roundness = smooth.roundness || 0.5;
        const cpX = midX + dy * roundness * 0.5;
        const cpY = midY - dx * roundness * 0.5;
        ctx.moveTo(x1, y1);
        ctx.quadraticCurveTo(cpX, cpY, x2, y2);
        ctx.stroke();
        // Midpoint of bezier is at t=0.5: (1-t)^2*P0 + 2*(1-t)*t*CP + t^2*P1
        midX = 0.25 * x1 + 0.5 * cpX + 0.25 * x2;
        midY = 0.25 * y1 + 0.5 * cpY + 0.25 * y2;

        // Arrow on curved edge
        if (arrowEnabled) {
          this._drawArrow(ctx, cpX, cpY, x2, y2, color, arrowScale, edge.to);
        }
      } else {
        // Straight line
        midX = (x1 + x2) / 2;
        midY = (y1 + y2) / 2;
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();

        // Arrow
        if (arrowEnabled) {
          this._drawArrow(ctx, x1, y1, x2, y2, color, arrowScale, edge.to);
        }
      }

      // Draw edge label only at high zoom
      if (edge.label && scale >= 1.25) {
        const fontSize = 11 / scale;
        ctx.font = `${fontSize}px Inter, sans-serif`;
        ctx.fillStyle = '#aaaaaa';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(edge.label, midX, midY);
      }
    }
  }

  _drawArrow(ctx, fromX, fromY, toX, toY, color, scaleFactor, toNodeId) {
    const engine = this._engine;
    const resolver = engine._groupStyleResolver;
    const nodeData = engine._nodes.get(toNodeId);
    const nodeRadius = nodeData ? resolver.resolve(nodeData).size : 25;

    const dx = toX - fromX;
    const dy = toY - fromY;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) return;

    const nx = dx / len;
    const ny = dy / len;

    // Place arrow tip at the node border
    const tipX = toX - nx * nodeRadius;
    const tipY = toY - ny * nodeRadius;

    const arrowLen = 10 * scaleFactor;
    const arrowWidth = 5 * scaleFactor;

    ctx.beginPath();
    ctx.fillStyle = color;
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(tipX - nx * arrowLen + ny * arrowWidth, tipY - ny * arrowLen - nx * arrowWidth);
    ctx.lineTo(tipX - nx * arrowLen - ny * arrowWidth, tipY - ny * arrowLen + nx * arrowWidth);
    ctx.closePath();
    ctx.fill();
  }

  _drawNodes(ctx, scale) {
    const engine = this._engine;
    const physics = engine._physics;
    const nodes = engine._nodes.get();
    const resolver = engine._groupStyleResolver;
    const selectedNodes = engine._interaction?._selectedNodes || new Set();
    const hoveredNode = engine._interaction?._hoveredNode;
    const opts = engine._options;
    const hoverBorderColor = opts.nodes?.color?.hover?.border || '#32CD32';
    const highlightBorderColor = opts.nodes?.color?.highlight?.border || '#32CD32';

    // Pre-compute edge counts per node
    const edges = engine._edges.get();
    const outCount = new Map();
    const inCount = new Map();
    for (let i = 0; i < edges.length; i++) {
      outCount.set(edges[i].from, (outCount.get(edges[i].from) || 0) + 1);
      inCount.set(edges[i].to, (inCount.get(edges[i].to) || 0) + 1);
    }

    // First pass: draw all node circles
    const labelsToDraw = [];
    let hasAnimatedNode = false;
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const idx = physics._idToIndex.get(node.id);
      if (idx === undefined) continue;

      const x = physics._pos[idx * 2];
      const y = physics._pos[idx * 2 + 1];
      const style = resolver.resolve(node);

      // Custom renderer (loading group)
      if (style.ctxRenderer) {
        hasAnimatedNode = true;
        ctx.save();
        style.ctxRenderer({ ctx, x, y, id: node.id });
        ctx.restore();
        continue;
      }

      const isSelected = selectedNodes.has(node.id);
      const isHovered = hoveredNode === node.id;

      let borderColor = style.borderColor;
      let bgColor = style.backgroundColor;
      let borderWidth = style.borderWidth;

      if (isSelected) {
        borderColor = highlightBorderColor;
        bgColor = opts.nodes?.color?.highlight?.background || bgColor;
        borderWidth = 2;
      } else if (isHovered) {
        borderColor = hoverBorderColor;
        bgColor = opts.nodes?.color?.hover?.background || bgColor;
      }

      // Draw node circle
      ctx.beginPath();
      ctx.arc(x, y, style.size, 0, Math.PI * 2);
      ctx.fillStyle = bgColor;
      ctx.fill();
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = borderWidth;
      ctx.stroke();

      // Collect label info for second pass
      if (node.label) {
        const minScale = style.size >= 80 ? 0 : style.size >= 40 ? 0.07 : style.size >= 25 ? 0.14 : 0.30;
        if (scale >= minScale) {
          labelsToDraw.push({ node, x, y, style, isSelected });
        }
      }
    }

    // Second pass: draw all labels on top of all nodes
    for (let i = 0; i < labelsToDraw.length; i++) {
      const { node, x, y, style, isSelected } = labelsToDraw[i];
      const fontColor = isSelected ? '#ffffff' : style.fontColor;
      const screenFontSize = style.fontSize / scale;
      ctx.font = `${screenFontSize}px ${style.fontFace}`;
      ctx.fillStyle = fontColor;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';

      const labelY = y + style.size + 5 / scale;

      if (isSelected) {
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1 / scale;
        ctx.strokeText(node.label, x, labelY);
      }
      ctx.fillText(node.label, x, labelY);

      // Draw badge row below the label: [#order] [out→] [→in]
      {
        const pills = [];
        // const out = outCount.get(node.id) || 0;
        // const inc = inCount.get(node.id) || 0;
        if (node.orderIndex != null) pills.push({ text: `#${node.orderIndex}`, bg: '#555555', fg: '#dddddd' });
        // page indicators hidden for now
        // if (node.sourcePages?.length > 0) {
        //   const pages = node.sourcePages;
        //   const pageLabel = pages.length <= 3 ? `p${pages.join(',')}` : `p${pages[0]}-${pages[pages.length - 1]}`;
        //   pills.push({ text: pageLabel, bg: '#3a3a4a', fg: '#aaaacc' });
        // }
        if (node.flashcardsCompleted) pills.push({ text: '\uf24d', bg: '#3a4a3a', fg: '#8bc88b', font: 'Font Awesome 7 Free' });
        // outgoing/incoming badges hidden for now (may re-enable for collapse feature)
        // if (out > 0) pills.push({ text: `↓${out}`, bg: '#555555', fg: '#dddddd' });
        // if (inc > 0) pills.push({ text: `↑${inc}`, bg: '#555555', fg: '#dddddd' });

        if (pills.length > 0) {
          const tagFontSize = (style.fontSize * 0.8) / scale;
          const defaultFont = `${tagFontSize}px ${style.fontFace}`;
          ctx.font = defaultFont;
          const pad = 5 / scale;
          const gap = 4 / scale;
          const tagY = labelY + screenFontSize + 6 / scale;

          // Measure text height from font metrics for accurate centering
          const metrics = ctx.measureText('M');
          const textH = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
          const tagH = textH + pad * 2;
          const radius = tagH / 2;

          // Measure total width to center the row
          let totalW = 0;
          const widths = pills.map(p => {
            if (p.font) ctx.font = `900 ${tagFontSize}px "${p.font}"`;
            const tw = ctx.measureText(p.text).width + pad * 2;
            if (p.font) ctx.font = defaultFont;
            totalW += tw;
            return tw;
          });
          totalW += gap * (pills.length - 1);

          let curX = x - totalW / 2;
          for (let p = 0; p < pills.length; p++) {
            const pill = pills[p];
            const pw = widths[p];
            ctx.fillStyle = pill.bg;
            ctx.beginPath();
            ctx.roundRect(curX, tagY, pw, tagH, radius);
            ctx.fill();
            if (pill.font) ctx.font = `900 ${tagFontSize}px "${pill.font}"`;
            ctx.fillStyle = pill.fg;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(pill.text, curX + pw / 2, tagY + tagH / 2);
            if (pill.font) ctx.font = defaultFont;
            curX += pw + gap;
          }
        }
      }
    }

    // Schedule continuous redraws for animated nodes (e.g. loading spinner)
    if (hasAnimatedNode) {
      clearTimeout(this._animTimer);
      this._animTimer = setTimeout(() => this.markDirty(), 100);
    }
  }

  destroy() {
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    clearTimeout(this._animTimer);
  }
}
